/**
 * Pilotage plan manager. Mirrors the route manager: it lists every clearing
 * bearing, the loose ones and those grouped under named plans. Tap one to open
 * its editor, show or hide a whole plan on the chart, rename a plan, or delete.
 * Clearing bearings are created by tapping a charted object and choosing "Set
 * clearing bearing"; here we organise them.
 */
import { useShellStore } from '@/store/shell';
import { usePilotageStore } from '@/store/pilotage';
import {
  clearingDisplayName,
  transitDisplayName,
  headingDisplayName,
  clearingDepthDisplayName,
  type ClearingBearing,
  type Transit,
  type HeadingBearing,
  type ClearingDepth,
} from '@/pilotage';
import { Sheet } from './Sheet';

function ClearingRow({
  c,
  onOpen,
  onDelete,
  dimmed,
}: {
  c: ClearingBearing;
  onOpen: () => void;
  onDelete: () => void;
  dimmed?: boolean;
}) {
  return (
    <div className="acp-route-row" style={dimmed ? { opacity: 0.5 } : undefined}>
      <button type="button" className="acp-route-sel" onClick={onOpen}>
        <b>{clearingDisplayName(c)}</b>
        <span>
          {c.alarm ? 'guarding' : 'guard off'} · safe side {c.safeSide}
          {c.anchorLabel ? ` · on ${c.anchorLabel}` : ''}
        </span>
      </button>
      <button type="button" className="acp-btn" onClick={onDelete}>
        Delete
      </button>
    </div>
  );
}

function TransitRow({
  t,
  onOpen,
  onDelete,
  dimmed,
}: {
  t: Transit;
  onOpen: () => void;
  onDelete: () => void;
  dimmed?: boolean;
}) {
  return (
    <div className="acp-route-row" style={dimmed ? { opacity: 0.5 } : undefined}>
      <button type="button" className="acp-route-sel" onClick={onOpen}>
        <b>{transitDisplayName(t)}</b>
        <span>transit{t.aLabel && t.bLabel ? ` · ${t.aLabel} & ${t.bLabel}` : ''}</span>
      </button>
      <button type="button" className="acp-btn" onClick={onDelete}>
        Delete
      </button>
    </div>
  );
}

function HeadingRow({
  h,
  onOpen,
  onDelete,
  dimmed,
}: {
  h: HeadingBearing;
  onOpen: () => void;
  onDelete: () => void;
  dimmed?: boolean;
}) {
  return (
    <div className="acp-route-row" style={dimmed ? { opacity: 0.5 } : undefined}>
      <button type="button" className="acp-route-sel" onClick={onOpen}>
        <b>{headingDisplayName(h)}</b>
        <span>
          {h.kind === 'back' ? 'back bearing' : 'heading bearing'}
          {h.anchorLabel ? ` · on ${h.anchorLabel}` : ''}
        </span>
      </button>
      <button type="button" className="acp-btn" onClick={onDelete}>
        Delete
      </button>
    </div>
  );
}

function ClearingDepthRow({
  d,
  onOpen,
  onDelete,
  dimmed,
}: {
  d: ClearingDepth;
  onOpen: () => void;
  onDelete: () => void;
  dimmed?: boolean;
}) {
  return (
    <div className="acp-route-row" style={dimmed ? { opacity: 0.5 } : undefined}>
      <button type="button" className="acp-route-sel" onClick={onOpen}>
        <b>{clearingDepthDisplayName(d)}</b>
        <span>do-not-cross depth{d.alarm ? '' : ' · guard off'}</span>
      </button>
      <button type="button" className="acp-btn" onClick={onDelete}>
        Delete
      </button>
    </div>
  );
}

export function PilotageSheet() {
  const clearings = usePilotageStore((s) => s.clearings);
  const plans = usePilotageStore((s) => s.plans);
  const hiddenPlanIds = usePilotageStore((s) => s.hiddenPlanIds);
  const togglePlanHidden = usePilotageStore((s) => s.togglePlanHidden);
  const renamePlan = usePilotageStore((s) => s.renamePlan);
  const removePlan = usePilotageStore((s) => s.removePlan);
  const addPlan = usePilotageStore((s) => s.addPlan);
  const setActiveClearing = usePilotageStore((s) => s.setActiveClearing);
  const removeClearing = usePilotageStore((s) => s.removeClearing);
  const transits = usePilotageStore((s) => s.transits);
  const setActiveTransit = usePilotageStore((s) => s.setActiveTransit);
  const removeTransit = usePilotageStore((s) => s.removeTransit);
  const openSheet = useShellStore((s) => s.openSheet);
  const showToast = useShellStore((s) => s.showToast);
  const armTransit = useShellStore((s) => s.armTransit);
  const headingBearings = usePilotageStore((s) => s.headingBearings);
  const setActiveHeadingBearing = usePilotageStore((s) => s.setActiveHeadingBearing);
  const removeHeadingBearing = usePilotageStore((s) => s.removeHeadingBearing);
  const armHeading = useShellStore((s) => s.armHeading);
  const clearingDepths = usePilotageStore((s) => s.clearingDepths);
  const setActiveClearingDepth = usePilotageStore((s) => s.setActiveClearingDepth);
  const removeClearingDepth = usePilotageStore((s) => s.removeClearingDepth);

  const loose = clearings.filter((c) => c.planId === null);
  const looseTransits = transits.filter((t) => t.planId === null);
  const looseHeadings = headingBearings.filter((h) => h.planId === null);
  const looseDepths = clearingDepths.filter((d) => d.planId === null);

  function openClearing(id: string) {
    setActiveClearing(id);
    openSheet('clearing');
  }
  function openTransit(id: string) {
    setActiveTransit(id);
    openSheet('transit');
  }
  function openHeading(id: string) {
    setActiveHeadingBearing(id);
    openSheet('heading');
  }
  function openClearingDepth(id: string) {
    setActiveClearingDepth(id);
    openSheet('clearingdepth');
  }

  return (
    <Sheet
      name="pilotage"
      title="Pilotage plan"
      sub="Your clearing bearings, loose or grouped into plans. Tap one to edit it; use the eye to show or hide a plan on the chart. Create new ones by tapping a charted object."
    >
      <div className="acp-btn-row">
        <button
          type="button"
          className="acp-btn primary full"
          onClick={() => {
            armTransit();
            showToast('Transit: tap the back (rear) mark, then the front');
          }}
        >
          Add transit
        </button>
      </div>
      <div className="acp-btn-row">
        <button
          type="button"
          className="acp-btn full"
          onClick={() => {
            armHeading('heading');
            showToast('Tap the object or point ahead — switch to a back bearing in the editor');
          }}
        >
          Add heading/back bearing
        </button>
      </div>
      {plans.length === 0 &&
        loose.length === 0 &&
        looseTransits.length === 0 &&
        looseHeadings.length === 0 &&
        looseDepths.length === 0 && (
        <p className="acp-empty">
          No clearing bearings yet. Tap a charted object (a buoy, beacon, or light) and choose “Set
          clearing bearing”, then come back here to group them into a plan.
        </p>
      )}

      {(loose.length > 0 ||
        looseTransits.length > 0 ||
        looseHeadings.length > 0 ||
        looseDepths.length > 0) && (
        <>
          <div className="acp-fieldlabel">Loose pilotage marks</div>
          {loose.map((c) => (
            <ClearingRow
              key={c.id}
              c={c}
              onOpen={() => openClearing(c.id)}
              onDelete={() => {
                removeClearing(c.id);
                showToast('Deleted');
              }}
            />
          ))}
          {looseTransits.map((t) => (
            <TransitRow
              key={t.id}
              t={t}
              onOpen={() => openTransit(t.id)}
              onDelete={() => {
                removeTransit(t.id);
                showToast('Deleted');
              }}
            />
          ))}
          {looseHeadings.map((h) => (
            <HeadingRow
              key={h.id}
              h={h}
              onOpen={() => openHeading(h.id)}
              onDelete={() => {
                removeHeadingBearing(h.id);
                showToast('Deleted');
              }}
            />
          ))}
          {looseDepths.map((d) => (
            <ClearingDepthRow
              key={d.id}
              d={d}
              onOpen={() => openClearingDepth(d.id)}
              onDelete={() => {
                removeClearingDepth(d.id);
                showToast('Deleted');
              }}
            />
          ))}
        </>
      )}

      {plans.map((p) => {
        const members = clearings.filter((c) => c.planId === p.id);
        const planTransits = transits.filter((t) => t.planId === p.id);
        const planHeadings = headingBearings.filter((h) => h.planId === p.id);
        const planDepths = clearingDepths.filter((d) => d.planId === p.id);
        const hidden = hiddenPlanIds.includes(p.id);
        return (
          <div key={p.id} style={{ marginTop: 12 }}>
            <div className="acp-route-row">
              <input
                className="acp-text-input"
                style={{ flex: 1 }}
                value={p.name}
                onChange={(e) => renamePlan(p.id, e.target.value)}
                aria-label="Plan name"
              />
              <button
                type="button"
                className={`acp-sw${hidden ? '' : ' on'}`}
                aria-pressed={!hidden}
                aria-label={`${hidden ? 'Show' : 'Hide'} ${p.name} on the chart`}
                title={hidden ? 'Hidden — tap to show on the chart' : 'Shown — tap to hide'}
                onClick={() => togglePlanHidden(p.id)}
              />
              <button
                type="button"
                className="acp-btn"
                onClick={() => {
                  removePlan(p.id);
                  showToast('Plan deleted');
                }}
              >
                Delete
              </button>
            </div>
            {members.length === 0 &&
            planTransits.length === 0 &&
            planHeadings.length === 0 &&
            planDepths.length === 0 ? (
              <p className="acp-empty">Empty. Open a clearing bearing or transit and file it here.</p>
            ) : (
              <>
                {members.map((c) => (
                  <ClearingRow
                    key={c.id}
                    c={c}
                    dimmed={hidden}
                    onOpen={() => openClearing(c.id)}
                    onDelete={() => {
                      removeClearing(c.id);
                      showToast('Deleted');
                    }}
                  />
                ))}
                {planTransits.map((t) => (
                  <TransitRow
                    key={t.id}
                    t={t}
                    dimmed={hidden}
                    onOpen={() => openTransit(t.id)}
                    onDelete={() => {
                      removeTransit(t.id);
                      showToast('Deleted');
                    }}
                  />
                ))}
                {planHeadings.map((h) => (
                  <HeadingRow
                    key={h.id}
                    h={h}
                    dimmed={hidden}
                    onOpen={() => openHeading(h.id)}
                    onDelete={() => {
                      removeHeadingBearing(h.id);
                      showToast('Deleted');
                    }}
                  />
                ))}
                {planDepths.map((d) => (
                  <ClearingDepthRow
                    key={d.id}
                    d={d}
                    dimmed={hidden}
                    onOpen={() => openClearingDepth(d.id)}
                    onDelete={() => {
                      removeClearingDepth(d.id);
                      showToast('Deleted');
                    }}
                  />
                ))}
              </>
            )}
          </div>
        );
      })}

      <div className="acp-btn-row">
        <button
          type="button"
          className="acp-btn full"
          onClick={() => {
            addPlan();
            showToast('New plan');
          }}
        >
          New plan
        </button>
      </div>
    </Sheet>
  );
}
